
import { Options, Vue } from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import { TabItemsMap } from '@/definitions/app/tab.items';
import { BodyEvent, CarCard, CarCardAttachment, CarEvent, FaceEvent, HumanCard, HumanCardAttachment } from '@/api';
import { aclModule } from '@/store/acl';
import { CardsMultiToSingle, CardType } from '@/store/application/data.assets';
import { cardAssetsModule } from '@/store/cards/CardAssetsModule';
import { AttachmentModule } from '@/store/data/AttachmentModule';
import { NForm } from '@/uikit';
import { NAttachment, NAttachmentAttachDoneHandler, NAttachmentRemoveDoneHandler, NAttachments, NAttachmentsI18n } from '@/uikit/attachments';
import NButton from '@/uikit/buttons/NButton.vue';
import NThumbnail from '@/uikit/thumbnail/NThumbnail.vue';
import { getItemCommonFields } from '@/components/common/get-item-common-fields';
import EventItem from '@/components/events/EventItem.vue';
import BatchLoaderModal from '@/pages/cards/batch-loader/BatchLoaderModal.vue';
import { getCardFormSchema } from '@/pages/cards/content/card-form-schema';
import { dataModule } from '@/store/data';
import { batchLoaderModule } from '@/pages/cards/batch-loader/BatchLoaderModule';

@Options({
  components: {
    BatchLoaderModal,
    EventItem,
    NAttachments,
    NButton,
    NForm,
    NThumbnail
  }
})
export default class CardForm extends Vue {
  @Prop({ type: Object, required: true })
  readonly item!: HumanCard | CarCard;

  @Prop({ type: Object, required: false })
  readonly event?: FaceEvent | BodyEvent | CarEvent;

  @Prop({ type: String, required: false })
  readonly objects?: string;

  @Prop({ type: Boolean, required: true })
  readonly disabled = false;

  @Prop({ type: String, required: true })
  readonly cardType!: CardType;

  @Prop({ type: String, required: true })
  readonly dataQa!: string;

  attachmentModule: AttachmentModule<CarCardAttachment | HumanCardAttachment, unknown> | null = null;
  attachments: NAttachment[] = [];

  get isDataModuleReady() {
    return dataModule.reactiveData.ready;
  }

  get form() {
    return this.$refs.itemForm;
  }

  get infoFields() {
    return getItemCommonFields();
  }

  get isCreated() {
    return this.item.id > 0;
  }

  async reloadAttachments() {
    if (!this.isCreated) {
      return;
    }
    this.attachmentModule = new AttachmentModule({
      url: `/${CardsMultiToSingle[this.cardType]}-card-attachments/`,
      source_id: this.item.id,
      source_form_field_name: 'card',
      source_filter_name: 'card'
    });
    await this.attachmentModule.loadAttachments();
    this.attachments = (this.attachmentModule.module.items || []).map((attachment) => ({
      file: attachment.file,
      id: attachment.id,
      name: attachment.name ?? '',
      progress: 100,
      size: attachment.size,
      status: 'done'
    }));
  }

  @Watch('cardType')
  cardTypeWatcher() {
    this.reloadAttachments();
  }

  @Watch('item.id')
  itemIdWatcher() {
    this.reloadAttachments();
  }

  mounted() {
    this.reloadAttachments();
  }

  get attachmentLabels(): NAttachmentsI18n {
    return {
      attach_first_one: this.$t('common.attach_first_one'),
      attach_more: this.$t('common.attach_more'),
      cancel: this.$t('common.cancel', 'f'),
      files: this.$tm('common.files_mf', { count: this.attachments.length }),
      no_files: this.$t('common.no_files'),
      removing_confirmation: this.$t('common.removing_confirmation'),
      yes: this.$t('common.yes', 'f')
    };
  }

  get schema() {
    return getCardFormSchema(this.cardType);
  }

  get metaSchema() {
    const metaFormItems = cardAssetsModule.getMetaFormItemsByTab(TabItemsMap.Info, this.cardType);
    return metaFormItems;
  }

  get hasUploadPermission() {
    const object = CardsMultiToSingle[this.cardType];
    return aclModule.getAccess(`ffsecurity.change_${object}card`);
  }

  get hasBatchUploadPermissions() {
    return aclModule.getAccess('ffsecurity.batchupload_cards') && aclModule.getAccess('ffsecurity.add_uploadlist');
  }

  handleAttachmentAttach(attachment: NAttachment, done: NAttachmentAttachDoneHandler): void {
    this.attachmentModule?.uploadAttachmentHandler(attachment).finally(done);
  }

  handleAttachmentRemove(attachment: NAttachment, done: NAttachmentRemoveDoneHandler): void {
    this.attachmentModule?.deleteAttachmentHandler(attachment).finally(done);
  }

  createBatchLoaderModal() {
    batchLoaderModule.createBatchUploadModal();
  }
}
